import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { logWarning } from "@faire/web--source/common/logging";
import { trackFrontendApisPrefetchMissView } from "@faire/web-api--source/events/frontendApis/view/prefetchMiss";
import { IApiError } from "@faire/web-api--source/indigofair/data/IApiError";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import { WebApiNetworkError } from "@faire/web-api--source/WebApiNetworkError";
import { emitDifferences, isSameRequest } from "../../__internal__/isSameRequest";
/**
 * Factory for wrapping a request handler with a prefetch-reader, that checks for
 * an equivalent request in faire.api.prefetches to return.
 * @param delegate Request handler to invoke when no prefetches are present
 */ export var prefetchReaderFactory = function(delegate) {
    return /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(requestOptions) {
            var prefetches, prefetchIndex, mismatch, _mismatch_request_route, _prefetches_splice, _ref, prefetch, response, _requestOptions_baseUrl, _requestOptions_baseUrl1, rawResponse, responseStatus, _prefetch_request_baseUrl, e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        // TODO(luke): Drop this cast once web-api updates.
                        if (requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.prefetch) {
                            return [
                                2,
                                delegate(requestOptions)
                            ];
                        }
                        prefetches = getGlobalProperty("faire.api.prefetches");
                        if (!(prefetches === null || prefetches === void 0 ? void 0 : prefetches.length)) {
                            return [
                                2,
                                delegate(requestOptions)
                            ];
                        }
                        prefetchIndex = prefetches.findIndex(function(p) {
                            return isSameRequest(p.request, requestOptions);
                        });
                        if (prefetchIndex < 0) {
                            mismatch = prefetches.find(function(p) {
                                return samePathAndMethod(p.request, requestOptions);
                            });
                            if (mismatch) {
                                ;
                                trackFrontendApisPrefetchMissView((_mismatch_request_route = mismatch.request.route) !== null && _mismatch_request_route !== void 0 ? _mismatch_request_route : "");
                                logWarning("Found differing requests for ".concat(mismatch.request.method, " ").concat(mismatch.request.route), {
                                    data: {
                                        prefetch: mismatch,
                                        request: requestOptions
                                    }
                                });
                                emitDifferences(requestOptions, mismatch.request);
                            }
                            return [
                                2,
                                delegate(requestOptions)
                            ];
                        }
                        _ref = _sliced_to_array((_prefetches_splice = prefetches === null || prefetches === void 0 ? void 0 : prefetches.splice(prefetchIndex, 1)) !== null && _prefetches_splice !== void 0 ? _prefetches_splice : [], 1), prefetch = _ref[0];
                        response = prefetch === null || prefetch === void 0 ? void 0 : prefetch.response;
                        if (!response) {
                            ;
                            logWarning("Prefetch for ".concat((_requestOptions_baseUrl = requestOptions.baseUrl) !== null && _requestOptions_baseUrl !== void 0 ? _requestOptions_baseUrl : "").concat(requestOptions.url, " has no response"), {
                                data: {
                                    prefetch: prefetch
                                }
                            });
                            return [
                                2,
                                delegate(requestOptions)
                            ];
                        }
                        // eslint-disable-next-line no-console
                        console.log("Leveraging prefetch for ".concat(requestOptions.method, " ").concat((_requestOptions_baseUrl1 = requestOptions.baseUrl) !== null && _requestOptions_baseUrl1 !== void 0 ? _requestOptions_baseUrl1 : "").concat(requestOptions.url));
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            ,
                            4
                        ]);
                        return [
                            4,
                            response
                        ];
                    case 2:
                        rawResponse = _state.sent();
                        if ("error" in response) {
                            return [
                                2,
                                response
                            ];
                        }
                        responseStatus = rawResponse.status;
                        if (!!responseStatus && responseStatus >= 400) {
                            ;
                            logFailedResponse("Prefetch of ".concat(prefetch.request.method, " ").concat((_prefetch_request_baseUrl = prefetch.request.baseUrl) !== null && _prefetch_request_baseUrl !== void 0 ? _prefetch_request_baseUrl : "").concat(prefetch.request.url, " failed with ").concat(responseStatus), prefetch.request, rawResponse);
                            // TODO(ggayowsky): Remove once RequestHandler always returns Response<T>
                            return [
                                2,
                                delegate(requestOptions)
                            ];
                        } else {
                            return [
                                2,
                                rawResponse
                            ];
                        }
                        return [
                            3,
                            4
                        ];
                    case 3:
                        e = _state.sent();
                        // TODO: Gracefully handle cases where resp.json() cannot parse the payload
                        // For now, since using fetch protocol for prefetch, assume that if awaiting the response results in an error,
                        // consider it a connection issue during the fetch as prefetech currently aren't cancellable
                        logFailedResponse(new WebApiNetworkError("Network Error during prefetch"), prefetch.request);
                        // TODO(ggayowsky): Remove once RequestHandler always returns Response<T>
                        return [
                            2,
                            delegate(requestOptions)
                        ];
                    case 4:
                        return [
                            2
                        ];
                }
            });
        });
        return function(requestOptions) {
            return _ref.apply(this, arguments);
        };
    }();
};
var logFailedResponse = function(messageOrError, request, response) {
    var error = messageOrError;
    if (typeof messageOrError === "string") {
        error = new WebApiError(messageOrError, response === null || response === void 0 ? void 0 : response.status, IApiError.build(response === null || response === void 0 ? void 0 : response.data));
    }
    logWarning(error, {
        data: {
            request: request,
            status: response === null || response === void 0 ? void 0 : response.status,
            headers: response === null || response === void 0 ? void 0 : response.headers
        }
    });
};
var samePathAndMethod = function(a, b) {
    var _a_method, _b_method;
    return a.url === b.url && ((_a_method = a.method) === null || _a_method === void 0 ? void 0 : _a_method.toLowerCase()) === ((_b_method = b.method) === null || _b_method === void 0 ? void 0 : _b_method.toLowerCase());
};
